import { Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContractRequest from "./routes/ContractRequest";
import MyRequests from "./routes/MyRequests";
import MyProfile from "./routes/MyProfile";
import SigningReturn from "./routes/SigningReturn";
import ReleaseSigningReturn from "./routes/ReleaseSigningReturn";
import MockDocusign from "./routes/MockDocusign";
import Countersign from "./routes/Countersign";
import Forgot from "./routes/Forgot";
import Login from "./routes/Login";
import Signup from "./routes/Signup";
import Reset from "./routes/Reset";
import Invite from "./routes/Invite";
import Admin from "./routes/Admin";
import Home from "./routes/Home";
import Onboarding from "./routes/Onboarding";
import AddState from "./routes/AddState";
import AddStateSigningReturn from "./routes/AddStateSigningReturn";
import RequireAuth from "./components/RequireAuth";
import RequireAgent from "./components/RequireAgent";
import RequireOnboarded from "./components/RequireOnboarded";
import configureAxios from "./lib/configAxios";
import axios from "axios";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import * as Sentry from "@sentry/react";
import { useBranding } from "./hooks/useBranding";

import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";

dayjs.extend(customParseFormat);
configureAxios(axios);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function App() {
  const { branding } = useBranding();
  const title = branding ? `${branding.name} | Contract Request Portal` : "Contract Request Portal";

  return (
    <div className="App">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={title} />
        {branding?.faviconPath && <link rel="icon" href={branding.faviconPath} />}
      </Helmet>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SentryRoutes>
            {/* Gated routes — anonymous access is not allowed. */}
            {/* #29 — agents are forced through onboarding before the dashboard
                or any flow; RequireOnboarded passes staff/onboarded agents. */}
            <Route path="/" element={<RequireAuth><RequireOnboarded><Home /></RequireOnboarded></RequireAuth>} />
            <Route path="/onboarding" element={<RequireAuth><RequireAgent><Onboarding /></RequireAgent></RequireAuth>} />
            <Route path="/add-state" element={<RequireAuth><RequireAgent><RequireOnboarded><AddState /></RequireOnboarded></RequireAgent></RequireAuth>} />
            <Route path="/add-state/signing-return/:id" element={<RequireAuth><RequireAgent><RequireOnboarded><AddStateSigningReturn /></RequireOnboarded></RequireAgent></RequireAuth>} />
            {/* #77 - agent dashboard pages: own submissions and read-only profile. */}
            <Route path="/my-requests" element={<RequireAuth><RequireAgent><RequireOnboarded><MyRequests /></RequireOnboarded></RequireAgent></RequireAuth>} />
            <Route path="/my-profile" element={<RequireAuth><RequireAgent><RequireOnboarded><MyProfile /></RequireOnboarded></RequireAgent></RequireAuth>} />
            <Route path="/contract" element={<RequireAuth><RequireAgent><RequireOnboarded><ContractRequest /></RequireOnboarded></RequireAgent></RequireAuth>} />
            {/* #11 — return from (and mock of) DocuSign signing. */}
            <Route path="/contract/signing-return/:id" element={<RequireAuth><RequireAgent><RequireOnboarded><SigningReturn /></RequireOnboarded></RequireAgent></RequireAuth>} />
            {/* #33 — return from signing one carrier's release letter. */}
            <Route path="/contract/release-signing-return/:id" element={<RequireAuth><RequireAgent><RequireOnboarded><ReleaseSigningReturn /></RequireOnboarded></RequireAgent></RequireAuth>} />
            <Route path="/mock-docusign/:id" element={<RequireAuth><MockDocusign /></RequireAuth>} />
            {/* #40 - principal/agency countersignature; the principal is not an app user, so no auth gate. */}
            <Route path="/countersign/:id" element={<Countersign />} />
            {/* #56 - the legacy GA Request flow is retired; send it to the regular contract request flow. */}
            <Route path="/ga-contract" element={<Navigate to="/contract" replace />} />
            {/* Admin self-gates (React Admin redirects to /login); leave as-is. */}
            <Route path="/admin/*" element={<Admin />} />
            {/* Public auth routes. */}
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/invite" element={<Invite />} />
          </SentryRoutes>
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
